import { Link } from "react-router-dom";
import { useState } from "react";
import logo from "#/assets/eatta-logo.png";
import { requestPasswordReset } from "#/features/auth/api";

function getApiErrorMessage(err: any) {
  return (
    err?.response?.data?.message ||
    err?.message ||
    "Failed to request password reset. Please try again."
  );
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await requestPasswordReset({ email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err: any) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-100 px-4">
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: `url(${logo})`,
          backgroundRepeat: "repeat",
          backgroundSize: "160px",
          backgroundPosition: "center",
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/80 via-white/60 to-slate-200/70" />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/70 bg-white/95 p-8 shadow-2xl backdrop-blur">
        <div className="mb-6 text-center">
          <img
            src={logo}
            alt="EATTA EMS"
            className="mx-auto mb-4 h-16 w-auto"
          />
          <h1 className="text-2xl font-bold text-slate-900">
            Forgot password
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Enter your email address. We will send you a reset link and OTP.
          </p>
        </div>

        {submitted ? (
          <div className="space-y-5">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              Reset instructions have been sent to your email.
            </div>

            <Link
              to="/login"
              className="block text-center text-sm font-semibold text-emerald-700 hover:underline"
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@eatta.co.ke"
                autoComplete="email"
                required
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? "Sending..." : "Send reset instructions"}
            </button>

            <Link
              to="/login"
              className="block text-center text-sm font-medium text-slate-600 hover:text-emerald-700 hover:underline"
            >
              Back to login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}